import { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';

type CompareMode = 'slider' | 'side' | 'toggle';
type Zoom = 'fit' | 1 | 2;

interface Props {
  beforeSrc: string | null;
  afterSvg: string | null;
  isProcessing: boolean;
}

export function Viewer({ beforeSrc, afterSvg, isProcessing }: Props) {
  const [mode, setMode] = useState<CompareMode>('side');
  const [showAfter, setShowAfter] = useState(true);
  const [pos, setPos] = useState(50);
  const [zoom, setZoom] = useState<Zoom>('fit');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code === 'Space' && mode === 'toggle') {
        e.preventDefault();
        setShowAfter((s) => !s);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode]);

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex items-center gap-2 border-b border-[var(--border)] p-2 text-sm">
        <button onClick={() => setZoom('fit')} className={btn(zoom === 'fit')}>Fit</button>
        <button onClick={() => setZoom(1)} className={btn(zoom === 1)}>100%</button>
        <button onClick={() => setZoom(2)} className={btn(zoom === 2)}>200%</button>
        <span className="mx-3 h-4 w-px bg-[var(--border)]" />
        <button onClick={() => setMode('slider')} className={btn(mode === 'slider')}>Slider</button>
        <button onClick={() => setMode('side')} className={btn(mode === 'side')}>Side-by-side</button>
        <button onClick={() => setMode('toggle')} className={btn(mode === 'toggle')}>Toggle (Space)</button>
      </div>

      <div className="relative flex-1 overflow-auto bg-[var(--entry)]">
        {isProcessing && (
          <>
            <div className="pointer-events-none absolute inset-x-0 top-0 z-20 h-0.5 overflow-hidden bg-[var(--border)]">
              <div className="h-full w-1/3 animate-[vektor-indeterminate_1.1s_ease-in-out_infinite] bg-[var(--content)]" />
            </div>
            <div className="tech-label pointer-events-none absolute right-3 top-3 z-20 flex items-center gap-2 rounded-sm border border-[var(--border)] bg-[var(--theme)]/90 px-3 py-1.5 shadow">
              <span className="h-2 w-2 animate-pulse rounded-sm bg-[var(--content)]" />
              Tracing
            </div>
            <style>{`@keyframes vektor-indeterminate { 0% { transform: translateX(-100%); } 100% { transform: translateX(400%); } }`}</style>
          </>
        )}
        {!beforeSrc && (
          <div className="blueprint-grid flex h-full items-center justify-center text-[var(--secondary)]">
            <span className="tech-label">No image yet</span>
          </div>
        )}

        {beforeSrc && mode === 'side' && (
          <div className="grid h-full grid-cols-2 gap-2 p-2">
            <Pane zoom={zoom}><Raster src={beforeSrc} zoom={zoom} /></Pane>
            <Pane zoom={zoom}><SvgPane svg={afterSvg} zoom={zoom} /></Pane>
          </div>
        )}

        {beforeSrc && mode === 'toggle' && (
          <Pane zoom={zoom}>
            {showAfter && afterSvg
              ? <SvgPane svg={afterSvg} zoom={zoom} />
              : <Raster src={beforeSrc} zoom={zoom} />}
          </Pane>
        )}

        {beforeSrc && mode === 'slider' && (
          <SliderCompare beforeSrc={beforeSrc} afterSvg={afterSvg} pos={pos} setPos={setPos} zoom={zoom} />
        )}
      </div>
    </div>
  );
}

function SliderCompare({
  beforeSrc, afterSvg, pos, setPos, zoom,
}: { beforeSrc: string; afterSvg: string | null; pos: number; setPos: (n: number) => void; zoom: Zoom }) {
  return (
    <div className="relative h-full w-full">
      <Pane zoom={zoom}><Raster src={beforeSrc} zoom={zoom} /></Pane>
      <div
        className="pointer-events-none absolute inset-0"
        style={{ clipPath: `inset(0 0 0 ${pos}%)` }}
      >
        <Pane zoom={zoom}><SvgPane svg={afterSvg} zoom={zoom} /></Pane>
      </div>
      <div
        className="pointer-events-none absolute inset-y-0 w-px bg-[var(--content)]/60"
        style={{ left: `${pos}%` }}
      >
        <div className="absolute top-1/2 left-1/2 flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-[var(--content)] bg-[var(--theme)] shadow">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="-ml-1">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </div>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        step={0.1}
        value={pos}
        onChange={(e) => setPos(Number(e.target.value))}
        className="absolute inset-0 h-full w-full cursor-ew-resize appearance-none bg-transparent opacity-0"
        aria-label="Compare slider"
      />
    </div>
  );
}

function Pane({ children, zoom }: { children: React.ReactNode; zoom: Zoom }) {
  const fit = zoom === 'fit';
  return (
    <div className={`flex h-full w-full ${fit ? 'items-center justify-center overflow-hidden' : 'overflow-auto'}`}>
      {children}
    </div>
  );
}

function Raster({ src, zoom }: { src: string; zoom: Zoom }) {
  if (zoom === 'fit') return <img src={src} alt="" className="h-full w-full object-contain" draggable={false} />;
  return <img src={src} alt="" style={{ width: `${zoom * 100}%`, height: 'auto' }} draggable={false} />;
}

function SvgPane({ svg, zoom }: { svg: string | null; zoom: Zoom }) {
  // The traced/passthrough SVG is untrusted (it can come straight from a
  // user-supplied SVG file via the passthrough path, and SVGO is an optimizer,
  // not a sanitizer). Sanitize at the injection sink so every render path —
  // including "SVGO: off" — is covered before it reaches the DOM.
  const safeSvg = useMemo(
    () =>
      svg
        ? DOMPurify.sanitize(svg, { USE_PROFILES: { svg: true, svgFilters: true } })
        : '',
    [svg],
  );
  if (!svg) return <div className="m-auto text-[var(--secondary)]">Processing…</div>;
  const className =
    zoom === 'fit'
      ? 'h-full w-full [&_svg]:h-full [&_svg]:w-full'
      : '[&_svg]:block';
  const style =
    zoom === 'fit'
      ? undefined
      : { width: `${zoom * 100}%`, height: 'auto' };
  return (
    <div className={className} style={style} dangerouslySetInnerHTML={{ __html: safeSvg }} />
  );
}

function btn(active: boolean) {
  return `rounded-sm px-2.5 py-1 text-sm transition ${
    active
      ? 'bg-[var(--content)] font-medium text-[var(--content-fg)]'
      : 'text-[var(--primary)] hover:bg-[var(--tertiary)]'
  }`;
}
